package com.crowpay.views.dialogs

import com.crowpay.Address
import com.crowpay.Owner
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.address
import com.crowpay.birthDate
import com.crowpay.extensions.isNotBlank
import com.crowpay.legalFirstName
import com.crowpay.legalLastName
import com.crowpay.ownership
import com.crowpay.sdk.notNullSession
import com.crowpay.ssn
import com.crowpay.ssnDisplay
import com.crowpay.title
import com.crowpay.utils.validation.Validator
import com.crowpay.utils.validation.validate
import com.crowpay.views.components.addressForm
import com.crowpay.views.components.files.FilePreview
import com.crowpay.views.components.files.renderFilePreview
import com.crowpay.views.components.label2
import com.crowpay.views.components.requiredField3
import com.crowpay.views.components.requiredLabel2
import com.crowpay.views.theming.body
import com.crowpay.views.theming.boldSubTitle
import com.crowpay.views.theming.primaryButton
import com.crowpay.views.theming.title
import com.lightningkite.kiteui.ExternalServices
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.KeyboardHints
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.Screen
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.reactive.Draft
import com.lightningkite.kiteui.reactive.LazyProperty
import com.lightningkite.kiteui.reactive.await
import com.lightningkite.kiteui.reactive.awaitNotNull
import com.lightningkite.kiteui.reactive.awaitOnce
import com.lightningkite.kiteui.reactive.bind
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.lens
import com.lightningkite.kiteui.reactive.modify
import com.lightningkite.kiteui.reactive.reactiveScope
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.buttonTheme
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.dialog
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.dismissBackground
import com.lightningkite.kiteui.views.direct.localDateField
import com.lightningkite.kiteui.views.direct.numberInput
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.scrolls
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.direct.stack
import com.lightningkite.kiteui.views.direct.textField
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.serialization.lensPath
import kotlinx.coroutines.launch

class CreateOrEditOwner(
    val owner: Owner?,
    val onSubmit: (suspend (draft: Owner) -> Unit)? = null
) : Page, Validator() {
    val draft = Draft(
        owner ?: Owner(
            legalFirstName = "",
            legalLastName = "",
            ownership = 100,
            address = Address(),
            ssn = "",
            document = null,
            birthDate = null
        )
    )

    val firstName = draft.lensPath { it.legalFirstName }.validateNotBlank()
    val lastName = draft.lensPath { it.legalLastName }.validateNotBlank()
    val address = draft.lensPath { it.address }.validate { it.isNotBlank() }
    val ownerTitle = draft.lensPath { it.title }.validateNotBlank()
    val ssnDisplay = draft.lensPath { it.ssnDisplay}
    val ssn = draft.lensPath { it.ssn }.validateNotBlank()
    val dob = draft.lensPath { it.birthDate }
    val ownership = draft.lensPath { it.ownership }.lens(
        get = { it.toDouble().takeUnless { it == 0.0 } },
        set = { it?.toInt() ?: 0 }
    )
    val displayImage = LazyProperty {
        draft().document?.let { FilePreview.RemoteFile(it.location) }
            ?: FilePreview.Empty
    }

    override fun ViewWriter.render2(): ViewModifiable {
        launch {
            ssn.set(ssnDisplay())
        }
        return dismissBackground {
            spacing = AppDimensions.fullIndent
            centered - sizeConstraints(width = 55.rem, height = 70.rem) - dialog - scrolls - stack {
                spacing = AppDimensions.fullIndent
                col {
                    title {
                        ::content {
                            if (owner == null) "Create Owner" else "Edit Owner"
                        }
                    }
                    requiredField3("Legal First Name", "Enter the owner's legal first name.") {
                        validate(firstName) - textField {
                            hint = "Johnathan"
                            keyboardHints = KeyboardHints.title
                            content bind firstName
                        }
                    }
                    requiredField3("Legal Last Name", "Enter the owner's legal last name.") {
                        validate(lastName) - textField {
                            hint = "Smithson"
                            keyboardHints = KeyboardHints.title
                            content bind lastName
                        }
                    }
                    requiredLabel2("Home Address", "Residential address of the owner.") {
                        addressForm(address, this@CreateOrEditOwner)
                    }
                    space()
                    boldSubTitle("Identity & Verification")
                    requiredField3("Owner Title", "Job title or role in the business.") {
                        validate(ownerTitle) - textField {
                            hint = "Managing Member"
                            keyboardHints = KeyboardHints.title
                            content bind ownerTitle
                        }
                    }
                    requiredField3("Date of Birth", "Used for identity verification.") {
                        validate { dob() != null } - localDateField {
                            content bind dob
                        }
                    }
                    requiredField3(
                        "Social Security Number",
                        "Encrypted and stored securely. Required for U.S. individuals."
                    ) {
                        validate(ssn) - textField {
                            hint = "XXX-XX-XXXX"
                            keyboardHints = KeyboardHints.password
                            content bind ssn
                        }
                    }
                    requiredField3(
                        "Percentage of Ownership",
                        "Enter ownership as a whole number percentage (e.g. 25)."
                    ) {
                        validate { ownership() != null && ownership()!! > 0.0 } - numberInput {
                            hint = "25"
                            keyboardHints = KeyboardHints.integer
                            range = 25.0..100.0
                            content bind ownership
                        }
                    }
                    label2(
                        "Upload Government ID (Primary Admin Only)",
                        "Only required for the individual setting up the account. Accept: PDF, JPG, PNG."
                    ) {
                        val value = displayImage.validate { it !is FilePreview.Empty }
                        button {
                            sizeConstraints(width = 6.rem, height = 6.rem) - stack {
                                reactiveScope {
                                    this@stack.clearChildren()
                                    val filePreview = value()

                                    renderFilePreview(filePreview, 6.rem)
                                }

                                body {
                                    ::exists{ value() == FilePreview.Empty }
                                    align = Align.Center
                                    content = "Image\nUpload"
                                }
                            }
                            onClick {
                                val result =
                                    ExternalServices.requestFile(
                                        listOf(
                                            "image/png",
                                            "image/jpeg",
                                            "application/pdf"
                                        )
                                    )
                                if (result != null) {
                                    FilePreview.LocalFile(result)
                                    value.set(FilePreview.LocalFile(result).also {
                                        it.upload(notNullSession().nonCached.contractor::uploadFileForRequest)
                                    })
                                }
                            }
                        }
                    }
                    row {
                        expanding - space()
                        buttonTheme - button {
                            body("Cancel")
                            onClick {
                                dialogScreenNavigator.dismiss()
                            }
                        }
                        primaryButton - button {
                            body("Save")
                            onClick {
                                val image = displayImage.awaitOnce()
                                if (image is FilePreview.LocalFile) {
                                    if (image.uploaded.await().awaitNotNull()) {
                                        draft.modify {
                                            it.copy(document = image.mainFile!!)
                                        }
                                    }
                                }
                                if (!draft().ssn.contains("*")) {
                                    draft.modify {
                                        it.copy(
                                            ssnDisplay = "*****${it.ssn.takeLast(4)}"
                                        )
                                    }
                                }
                                onSubmit?.invoke(draft())
                                dialogScreenNavigator.dismiss()
                            }
                        }
                    }
                    space()
                }
            }
        }
    }
}